/**
 * OrderbookFreshness — per-book "seconds since last orderbook frame" counter.
 *
 * Reads time since the last ORDERBOOK frame for a ticker
 * (marketStore.getOrderbookAgeMs), tracked separately from market/trade frames.
 * That separation matters: hydra's Kalshi orderbook-delta delivery can freeze a
 * book while the market-frame price keeps ticking, so a freshness signal built
 * on the general feed clock would lie.
 *
 * Shows a plain rolling age ("0s", "1s", "2s", ...) with NO live/stale coloring.
 * A Kalshi book can legitimately go quiet for several seconds, so flagging that
 * as "not live" is misleading — the data is genuinely current as of N seconds
 * ago. The number is the honest signal; the trader reads it against game
 * context. The only distinct state is "paused" — a confirmed server-side
 * suspension (hydra backpressure level >= 2), which is not a guess.
 *
 * Ticks every 1s so the age climbs on screen even when no frame arrives; also
 * subscribes to version so a fresh frame resets it immediately.
 */

import { useEffect, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';

export function OrderbookFreshness({ ticker }: { ticker?: string | null }) {
  const version = useMarketStore((s) => s.version);
  const paused = useMarketStore((s) => s.backpressureLevel >= 2);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1_000_000), 1_000);
    return () => clearInterval(id);
  }, []);

  if (!ticker) return null;
  void version; // re-read age on each ingested frame, not only the 1s tick

  const ageMs = useMarketStore.getState().getOrderbookAgeMs(ticker);

  let label: string;
  let title: string;
  let box: string;
  let dot: string;
  if (ageMs == null) {
    label = '—';
    title = 'No orderbook frame received for this market yet';
    box = 'bg-muted/40 text-muted-foreground';
    dot = 'bg-muted-foreground';
  } else if (paused) {
    label = 'paused';
    title = 'Hydra throttled this connection (backpressure ≥ 2) — orderbook frames suspended';
    box = 'bg-amber-500/15 text-amber-400';
    dot = 'bg-amber-400 animate-pulse';
  } else {
    const secs = Math.floor(ageMs / 1000);
    label = `${secs}s`;
    title = `${secs}s since the last orderbook frame for this market`;
    box = 'bg-muted/50 text-foreground/70';
    dot = 'bg-muted-foreground';
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${box}`}
      title={title}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
